import type { ComponentPropsWithAs } from '@/@types/common'
import type { User } from '@/@types/user'
import { Avatar as BaseAvatar } from '@mantine/core'
import { Link, type LinkProps } from '@tanstack/react-router'
import clsx from 'clsx'
import { get } from 'lodash-es'
import type { ComponentProps, ElementType } from 'react'
import styles from './Avatar.module.css'
import UserProfileHoverCard, { UserProfileHoverCardProps } from './UserProfileHoverCard'

export interface AvatarProps {
	data: User | undefined

	/**
	 * avatarKey
	 * @default 'thumbnailUrl'
	 */
	avatarKey?: string

	containerProps?: Partial<UserProfileHoverCardProps>
}

export default function Avatar<As extends ElementType = typeof Link>({
	as: AsComponent,
	data,
	avatarKey = 'thumbnailUrl',
	containerProps,
	className,
	...props
}: ComponentPropsWithAs<AvatarProps, As>) {
	const Component = AsComponent ?? Link
	let defaultProps = {} as LinkProps

	if (Component === Link) {
		defaultProps = {
			onClick: (e: MouseEvent) => {
				e.stopPropagation()
			},
			to: '/u/$username',
			params: {
				username: data?.username,
			},
		} as ComponentProps<As>
	}

	if (!data) {
		return null
	}

	return (
		<UserProfileHoverCard data={data} {...containerProps}>
			<Component className={clsx(styles.container, className)} {...defaultProps} {...props}>
				{data.decorations?.campaign?.picUrl && (
					<img
						loading="lazy"
						referrerPolicy="no-referrer"
						src={data.decorations.campaign.picUrl}
						className={clsx(`campaign-icon`, styles.campaignIcon)}
						alt=""
					/>
				)}
				<BaseAvatar
					size={42}
					src={get(data.avatarImage, avatarKey)}
					className={clsx(`jk-avatar`, styles.avatar)}
				/>
				{data.isSponsor && data.decorations?.sponsor?.picUrl && (
					<img
						width={14}
						height={14}
						loading="lazy"
						referrerPolicy="no-referrer"
						src={data.decorations?.sponsor?.picUrl}
						className={clsx(`sponsor-icon`, styles.sponsorIcon)}
						alt="sponsor"
					/>
				)}
			</Component>
		</UserProfileHoverCard>
	)
}
